import type { Subject} from 'rxjs';
import {
    BehaviorSubject,
    from,
    Observable,
    of,
    ReplaySubject
} from 'rxjs';
import type {Toggler} from 'shared/utils/custom-utils';
import {catchError, map, share} from 'rxjs/operators';

/**
 * @ngdoc type
 * @name addSubscriptionHooks
 * @module observables-utils
 * @kind function
 *
 * @template T
 *
 * @param {function(): void} onSubscribe Function to be invoked when subscribed
 * @param {function(): void} onUnsubscribe Function to be invoked when unsubscribed
 *
 * @returns {function(Observable<T>): Observable<T>}
 *
 * @description
 * Custom RxJs operator
 * Returns observable, that when subscribed to, subscribes to source observable and invokes onSubscribe hook function.
 * Returns observable, that when unsubscribed from, unsubscribes from source observable and invokes onUnsubscribe hook function.
 *
 * @deprecated - use `tap()` operator from rxjs with params object {subscribe, unsubscribe}
 */
export function addSubscriptionHooks(onSubscribe: () => void, onUnsubscribe: () => void) {
    return <T>(source: Observable<T>) => {
        const subscriptionLogic = (subscriber) => {
            onSubscribe();
            const subscription = source.subscribe(subscriber);
            return () => {
                subscription.unsubscribe();
                onUnsubscribe();
            }
        };
        return new Observable<T>((subscriber) => subscriptionLogic(subscriber));
    }
}

/**
 * @module observables-utils
 * @name ObservableToggler
 * @kind class
 *
 * @description
 * Wrapper class around Toggler object
 * It enables to observe current state via rxjs subscription
 */
export class ObservableToggler<T> {
    private state$: Subject<T>;

    constructor(private toggler: Toggler<T>) {
        this.state$ = new BehaviorSubject(this.toggler.getState())
    }

    public toggle() {
        this.toggler.toggle();
        this.state$.next(this.toggler.getState());
    }

    public getState(): T {
        return this.toggler.getState();
    }

    public getObservable(): Observable<T> {
        return this.state$.asObservable();
    }

    public reset(): void {
        this.toggler.reset();
        this.state$.next(this.toggler.getState());
    }
}

/**
 * @module observables-utils
 * @name PromiseResultWrapper
 * @kind interface
 *
 * @template T
 *
 * @description
 * Wrapper encapsulating result of a promise
 * data (from then) or error (from catch)
 */
export interface PromiseResultWrapper<T> {
    data?: T;
    error?: any;
}

/**
 * @module observables-utils
 * @name wrapPromiseResult
 * @kind function
 *
 * @template T
 *
 * @description
 * Creates observable from promise,
 * wrapping result into {@link PromiseResultWrapper}
 */
export function wrapPromiseResult<T>(promise: PromiseLike<T>): Observable<PromiseResultWrapper<T>> {
    return from(promise).pipe(
            map((data) => { return {data}}),
            catchError((error) => of({error})));
}

/**
 * @module observables-utils
 * @name unwrapPromiseResult
 * @kind function
 *
 * @template T
 *
 * @description
 * rxjs pipeable operator
 * unwraps data from {@link PromiseResultWrapper}
 * if is result errored rethrows original error
 * else pass original data
 */
export function unwrapPromiseResult<T>() {
    return (source: Observable<PromiseResultWrapper<T>>): Observable<T> => {
        return source.pipe(
            map((x) => {
                if (x?.error) {
                    throw x.error;
                } else {
                    return x.data
                }
            }))
    }
}

export function shareWithRefCountAndReplayLast() {
    return <T>(source: Observable<T>): Observable<T> => {
        return source.pipe(
            share({
                connector: () => new ReplaySubject(1),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: true
            })
        )
    }
}